import { unit } from "mathjs";

/* eslint-disable @typescript-eslint/no-magic-numbers -- multiple conversion values used in this file */

/**
 * 1 US Survey Foot is defined as 1200/3937 meter exactly:
 * https://www.nist.gov/pml/us-surveyfoot
 */
const METER_TO_US_SURVEY_FOOT = 3937 / 1200;
const SQUARE_METER_TO_SQUARE_US_SURVEY_FOOT = METER_TO_US_SURVEY_FOOT * METER_TO_US_SURVEY_FOOT;
/** 1 foot is defined as 0.3048 meter exactly */
const METER_TO_FOOT = 1 / 0.3048;
const SQUARE_METER_TO_SQUARE_FOOT = METER_TO_FOOT * METER_TO_FOOT;
const ABSOLUTE_PRECISION_IN_INCHES = 0.5 / 16;
const NON_BREAKING_SPACE = "\u00A0";
const PRIME_CHARACTER = "\u2032";
const DOUBLE_PRIME_CHARACTER = "\u2033";
const FRACTION_SLASH_CHARACTER = "\u2044";

/**
 * @param value Number to be converted to fraction
 * @param denominator the precision to be used as denominator
 * @returns the formatted fraction for a given number as [-][X][ ][Y/Z] with...
 * 1- optional "-" sign for negative numbers
 * 2- optional integer part X (for values almost >= 1)
 * 3- optional non breaking space (when both an integer part and fraction part are present)
 * 4- optional fraction part Y/Z (for values not close enough from an integer)
 */
export function formatFractionNumber(value, denominator) {
  if (denominator === 0) {
    throw new Error("formatFractionNumber: denominator cannot be zero");
  }
  const absoluteValue = Math.abs(value);
  let intPart = Math.trunc(absoluteValue);
  let numerator = Math.round((absoluteValue - intPart) * denominator);
  const absolutePrecision = 0.5 / denominator;

  // numerator and denominator are the same (or close enough), add it to the integer
  if (Math.abs(1 - Math.abs(numerator / denominator)) <= absolutePrecision) {
    numerator = 0;
    intPart += 1;
  } else {
    // reduce fraction
    // Not using the fraction function from mathjs (https://mathjs.org/docs/datatypes/fractions.html) because it returns improper fraction
    // and not the mixed fraction.
    for (let i = numerator; i > 1; i--) {
      if (denominator % i === 0 && numerator % i === 0) {
        denominator /= i;
        numerator /= i;
      }
    }
  }
  let result = value < -absolutePrecision ? "-" : "";
  if (intPart > 0 || numerator === 0) {
    result += intPart;
  }
  if (numerator > 0) {
    if (intPart > 0) {
      result += NON_BREAKING_SPACE;
    }
    result += `${numerator + FRACTION_SLASH_CHARACTER + denominator}`;
  }
  return result;
}

/**
 * Convert the input value in meters to nicely formatted label that can be used
 * for display only.
 *
 * @returns feet and inches in string, converted from given value in meters
 */
export function metersToFormattedFractional(distanceInMeters) {
  // get optional number of feet (integer) and number of inches (float)
  const [feet, inches] = unit(distanceInMeters, "m").splitUnit(["ft", "in"]);
  const inchesValue = Math.abs(inches.toNumber());

  // actual value of inches fixed for rounding to precision
  let fixedInchesValue;

  // actual value of feet fixed for rounding to precision
  let fixedFeetValue = Math.round(Math.abs(feet.toNumber()));
  if (inchesValue < ABSOLUTE_PRECISION_IN_INCHES) {
    // too close to 0 inches, reset inches
    fixedInchesValue = 0;
  } else if (Math.abs(inchesValue - 12) < ABSOLUTE_PRECISION_IN_INCHES) {
    // too close to 12 inches, increment feet and reset inches
    fixedInchesValue = 0;
    fixedFeetValue++;
  } else {
    // far enough from 12 inches, keep the value
    fixedInchesValue = inchesValue;
  }
  const signText = distanceInMeters < 0 && (fixedFeetValue || fixedInchesValue) ? "-" : "";
  const feetText = fixedFeetValue ? `${fixedFeetValue + PRIME_CHARACTER}` : "";
  const spaceText = fixedFeetValue && fixedInchesValue ? NON_BREAKING_SPACE : "";
  const fractionText = formatFractionNumber(fixedInchesValue, 16);
  const inchesFraction = fixedInchesValue || fixedFeetValue === 0 ? fractionText + DOUBLE_PRIME_CHARACTER : "";
  return signText + feetText + spaceText + inchesFraction;
}

/**
 * Converts a distance value from meters into the specified distance unit.
 *
 * @returns A string representation of the distance in the specified unit of measurement.
 * @param distanceInMeters The distance value in meters.
 * @param unitMeasurement The unit of measurement to convert the distance to.
 */
export function convertDistance(distanceInMeters, unitMeasurement) {
  switch (unitMeasurement) {
    case "millimeters":
      // Millimeters (integer) | 1234 mm
      return `${(distanceInMeters * 1000).toFixed(0)}\u00A0mm`;
    case "meters":
      // Meters (3 digits) | 1.234 m
      return `${distanceInMeters.toFixed(3)}\u00A0m`;
    case "feetFractionalInches":
      // Feet + Fractional Inches | 71′ 6 1⁄8″
      return metersToFormattedFractional(distanceInMeters);
    case "decimalFeet":
      // Decimal Feet (3 decimal places) | 71.125'
      return `${unit(distanceInMeters, "m").toNumber("ft").toFixed(3)}'`;
    case "decimalUSSurveyFeet":
      // Decimal US Survey Feet (3 decimal places) | 71.125 ft (US)
      return `${(distanceInMeters * METER_TO_US_SURVEY_FOOT).toFixed(3)}\u00A0ft\u00A0(US)`;
    case "fractionalInches":
      // Fractional Inches | 62 1⁄8″
      return `${formatFractionNumber(unit(distanceInMeters, "m").toNumber("in"), 16)}″`;
    case "decimalInches":
      // Decimal Inches (2 digits) | 62.13″
      return `${unit(distanceInMeters, "m").toNumber("in").toFixed(2)}″`;
  }
}

/**
 * Converts an area value from square meters into the specified area unit.
 *
 * @returns A string representation of the area in the specified are unit.
 * @param areaInMetersSq The area value in square meters.
 * @param unitMeasurement The unit of measurement to convert the area to.
 */
export function convertArea(areaInMetersSq, unitMeasurement) {
  switch (unitMeasurement) {
    case "millimeters":
    case "meters":
      return `${areaInMetersSq.toFixed(3)}\u00A0m²`;
    case "feetFractionalInches":
    case "decimalFeet":
    case "fractionalInches":
    case "decimalInches":
      return `${(areaInMetersSq * SQUARE_METER_TO_SQUARE_FOOT).toFixed(3)}\u00A0ft²`;
    case "decimalUSSurveyFeet":
      return `${(areaInMetersSq * SQUARE_METER_TO_SQUARE_US_SURVEY_FOOT).toFixed(3)}\u00A0ft²\u00A0(US)`;
  }
}

/** Props for {@link getPreferredAreaUnit}. */

/** Props for {@link convertAreaAmount}. */

/**
 * Determines the area unit that matches the user's preferred unit system.
 * Metric distance units (meters, millimeters) map to square meters, while all
 * imperial units map to square feet. When the user has no preferred unit set,
 * the region default is used: EU defaults to metric, COM to imperial.
 */
export function getPreferredAreaUnit({
  unitMeasurement,
  appRegion
}) {
  if (!unitMeasurement) {
    return appRegion === "eu" ? "sqm" : "sqft";
  }
  return unitMeasurement === "meters" || unitMeasurement === "millimeters" ? "sqm" : "sqft";
}

/**
 * Converts an area amount from one area unit to another.
 */
export function convertAreaAmount({
  amount,
  fromUnit,
  toUnit
}) {
  if (fromUnit === toUnit) {
    return amount;
  }
  return fromUnit === "sqft" ? amount / SQUARE_METER_TO_SQUARE_FOOT : amount * SQUARE_METER_TO_SQUARE_FOOT;
}

/* eslint-enable @typescript-eslint/no-magic-numbers */